import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PaidIcon from "@mui/icons-material/Paid";
import SavingsIcon from "@mui/icons-material/Savings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import LinkIcon from "@mui/icons-material/Link";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { motion } from "framer-motion";
import {
  createWeddingFinanceAttachment,
  createWeddingFinanceBudget,
  createWeddingFinanceCategory,
  createWeddingFinanceExpense,
  createWeddingFinancePayment,
  createWeddingFinanceQuote,
  getWeddingFinanceAttachments,
  getWeddingFinanceBudget,
  getWeddingFinanceCategories,
  getWeddingFinanceExpenses,
  getWeddingFinancePayments,
  getWeddingFinanceQuotes,
  updateWeddingFinanceBudget,
  updateWeddingFinanceCategory,
  updateWeddingFinanceExpense,
  updateWeddingFinancePayment,
  updateWeddingFinanceQuote,
} from "../../../utils/weddingFinanceApi";
import { useAuth } from "../../../contexts/AuthContext";
import "./FinanceTab.css";

const CURRENCY_OPTIONS = ["EUR", "USD", "MKD"];
const QUOTE_STATUSES = ["draft", "shortlisted", "selected", "rejected"];
const SHOW_PAYMENTS_PLANNER = true;
const ATTACHMENT_ENTITY_TYPES = SHOW_PAYMENTS_PLANNER
  ? ["expense", "quote", "payment"]
  : ["expense", "quote"];
const QUOTE_STATUS_LABELS = {
  draft: "Нацрт",
  shortlisted: "Во избор",
  selected: "Избрана",
  rejected: "Одбиена",
};
const PAYMENT_STATUS_LABELS = {
  planned: "Планирано",
  partially_paid: "Делумно платено",
  paid: "Платено",
  canceled: "Откажано",
};
const ENTITY_TYPE_LABELS = {
  expense: "Трошок",
  quote: "Понуда",
  payment: "Плаќање",
};
const DEFAULT_CATEGORY_BLUEPRINT = [
  { key: "venue", name: "Локација", weight: 0.34 },
  { key: "food", name: "Храна и пијалоци", weight: 0.21 },
  { key: "photo_video", name: "Фото и видео", weight: 0.12 },
  { key: "music", name: "Музика", weight: 0.1 },
  { key: "attire", name: "Облека", weight: 0.08 },
  { key: "decor", name: "Декорација", weight: 0.08 },
  { key: "other", name: "Останато", weight: 0.07 },
];

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const roundAmount = (value) => Math.round(toNumber(value) * 100) / 100;

const toCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

const toEntityId = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

const derivePaymentStatus = (amountPaid, amountDue) => {
  const paid = Math.max(0, roundAmount(amountPaid));
  const due = Math.max(0, roundAmount(amountDue));

  if (due <= 0 || paid <= 0) {
    return "planned";
  }

  if (paid >= due) {
    return "paid";
  }

  return "partially_paid";
};

const formatMoney = (amount, currency = "EUR") => {
  const normalized = roundAmount(amount);
  try {
    return new Intl.NumberFormat("mk-MK", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(normalized);
  } catch (error) {
    return `${normalized.toFixed(2)} ${currency}`;
  }
};

const toDateLabel = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("mk-MK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

const toInputDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const toInputDateTime = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const toIsoDateTime = (value) => {
  if (!value) {
    return new Date().toISOString();
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date().toISOString();
  }

  return date.toISOString();
};

const defaultValidUntilPlusOneMonth = () => {
  const date = new Date();
  date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
};

const toArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.items)) {
    return value.items;
  }

  if (Array.isArray(value?.data)) {
    return value.data;
  }

  if (Array.isArray(value?.attachments)) {
    return value.attachments;
  }

  return [];
};

const toBudget = (value) => {
  if (!value) {
    return null;
  }

  if (value.budget && typeof value.budget === "object") {
    return value.budget;
  }

  return value;
};

const isNotFoundError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("404") || message.includes("not found");
};

const buildAttachmentEntityPayload = (entityType, entityId) => ({
  expense_id: entityType === "expense" ? entityId : null,
  vendor_quote_id: entityType === "quote" ? entityId : null,
  payment_id: entityType === "payment" ? entityId : null,
});

const motionContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.06,
    },
  },
};

const motionItem = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.34 } },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FinanceTab = ({ paymentFocus = 0 }) => {
  const { weddingId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [budget, setBudget] = useState(null);
  const [categories, setCategories] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    budget: false,
    expenses: false,
    quotes: false,
    payments: false,
    attachments: false,
  });
  useEffect(() => {
    if (!loading && paymentFocus > 0) {
      setCollapsedSections((previous) => ({ ...previous, payments: false }));
      const section = document.getElementById("organizer-payments");
      section?.scrollIntoView?.({ block: "start" });
      section?.focus({ preventScroll: true });
    }
  }, [loading, paymentFocus]);

  const [categoryDrafts, setCategoryDrafts] = useState({});
  const [categoryNameDrafts, setCategoryNameDrafts] = useState({});
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [paymentDrafts, setPaymentDrafts] = useState({});
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);
  const [metricInfoAnchorEl, setMetricInfoAnchorEl] = useState(null);
  const [metricInfoText, setMetricInfoText] = useState("");
  const [quoteEditModalOpen, setQuoteEditModalOpen] = useState(false);
  const [expenseEditModalOpen, setExpenseEditModalOpen] = useState(false);

  const [budgetForm, setBudgetForm] = useState({
    currency: "EUR",
    total_budget: "",
    notes: "",
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    allocated_amount: "",
  });

  const [quoteForm, setQuoteForm] = useState({
    budget_category_id: "",
    vendor_name: "",
    amount: "",
    notes: "",
    status: "draft",
  });

  const [paymentForm, setPaymentForm] = useState({
    budget_category_id: "",
    vendor_quote_id: "",
    due_date: toInputDate(),
    amount_due: "",
    amount_paid: "0",
  });

  const [expenseForm, setExpenseForm] = useState({
    budget_category_id: "",
    vendor_quote_id: "",
    amount: "",
    spent_at: toInputDateTime(),
    notes: "",
  });

  const [attachmentForm, setAttachmentForm] = useState({
    entity_type: "expense",
    entity_id: "",
    file: null,
  });

  const [attachmentFilter, setAttachmentFilter] = useState({
    entity_type: "",
  });
  const [quoteEditForm, setQuoteEditForm] = useState({
    id: null,
    budget_category_id: "",
    vendor_name: "",
    amount: "",
    notes: "",
    status: "draft",
    valid_until: "",
  });
  const [expenseEditForm, setExpenseEditForm] = useState({
    id: null,
    budget_category_id: "",
    vendor_quote_id: "",
    amount: "",
    spent_at: toInputDateTime(),
    notes: "",
  });

  const loadFinanceData = useCallback(async ({ silent = false } = {}) => {
    if (!weddingId) {
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError("");

      const readMaybeUnavailable = async (reader, fallbackValue) => {
        try {
          return await reader();
        } catch (err) {
          if (isNotFoundError(err)) {
            return fallbackValue;
          }
          throw err;
        }
      };

      const budgetPayload = await readMaybeUnavailable(
        () => getWeddingFinanceBudget(weddingId, { allowNotFound: true }),
        null
      );
      const normalizedBudget = toBudget(budgetPayload);

      if (!normalizedBudget) {
        setBudget(null);
        setCategories([]);
        setQuotes([]);
        setPayments([]);
        setExpenses([]);
        setAttachments([]);
        return;
      }

      const [categoriesPayload, quotesPayload, paymentsPayload, expensesPayload] =
        await Promise.all([
          readMaybeUnavailable(() => getWeddingFinanceCategories(weddingId), []),
          readMaybeUnavailable(() => getWeddingFinanceQuotes(weddingId), []),
          readMaybeUnavailable(() => getWeddingFinancePayments(weddingId), []),
          readMaybeUnavailable(() => getWeddingFinanceExpenses(weddingId), []),
        ]);

      setBudget(normalizedBudget);
      setCategories(toArray(categoriesPayload));
      setQuotes(toArray(quotesPayload));
      setPayments(toArray(paymentsPayload));
      setExpenses(toArray(expensesPayload));

      if (normalizedBudget) {
        setBudgetForm((prev) => ({
          ...prev,
          currency: normalizedBudget.currency || prev.currency || "EUR",
          total_budget: String(roundAmount(normalizedBudget.total_budget)),
          notes: normalizedBudget.notes || "",
        }));
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно вчитување на финансиските податоци.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [weddingId]);

  const loadAttachments = useCallback(
    async (filters = {}) => {
      if (!weddingId || !budget) {
        setAttachments([]);
        return;
      }

      const normalizedFilters = {};
      if (filters?.entity_type) {
        normalizedFilters.entity_type = filters.entity_type;
      }

      const entityId = toEntityId(filters?.entity_id);
      if (entityId) {
        normalizedFilters.entity_id = entityId;
      }

      try {
        setAttachmentsLoading(true);
        const payload = await getWeddingFinanceAttachments(weddingId, normalizedFilters);
        setAttachments(toArray(payload));
      } catch (err) {
        if (isNotFoundError(err)) {
          setAttachments([]);
          return;
        }
        console.error(err);
        setError(err.message || "Неуспешно вчитување на прилозите.");
      } finally {
        setAttachmentsLoading(false);
      }
    },
    [weddingId, budget]
  );

  useEffect(() => {
    loadFinanceData();
  }, [loadFinanceData]);

  useEffect(() => {
    if (!budget) {
      setAttachments([]);
      return;
    }
    loadAttachments();
  }, [budget, loadAttachments]);

  useEffect(() => {
    const next = {};
    const nameNext = {};
    categories.forEach((category) => {
      if (!category?.id) {
        return;
      }
      next[category.id] = String(roundAmount(category.allocated_amount));
      nameNext[category.id] = String(category.name || category.key || "");
    });
    setCategoryDrafts(next);
    setCategoryNameDrafts(nameNext);
  }, [categories]);

  useEffect(() => {
    const next = {};
    payments.forEach((payment) => {
      if (!payment?.id) {
        return;
      }
      next[payment.id] = String(roundAmount(payment.amount_paid));
    });
    setPaymentDrafts(next);
  }, [payments]);

  const currency = budget?.currency || budgetForm.currency || "EUR";

  const aggregates = useMemo(() => {
    const budgetTotal = roundAmount(budget?.total_budget);
    const allocatedTotal = categories.reduce(
      (sum, category) => sum + roundAmount(category.allocated_amount),
      0
    );
    const spentTotal = expenses.reduce(
      (sum, expense) => sum + roundAmount(expense.amount),
      0
    );
    const paymentsDueTotal = payments.reduce(
      (sum, payment) => sum + roundAmount(payment.amount_due),
      0
    );
    const paymentsPaidTotal = payments.reduce(
      (sum, payment) => sum + roundAmount(payment.amount_paid),
      0
    );

    return {
      budgetTotal,
      allocatedTotal,
      spentTotal,
      paymentsDueTotal,
      paymentsPaidTotal,
      remainingBudget: roundAmount(budgetTotal - spentTotal),
      spendProgress:
        budgetTotal > 0
          ? Math.min((spentTotal / budgetTotal) * 100, 100)
          : 0,
    };
  }, [budget, categories, expenses, payments]);

  const expensesByCategory = useMemo(() => {
    const map = {};

    expenses.forEach((expense) => {
      const categoryId = expense?.budget_category_id;
      if (!categoryId) {
        return;
      }

      const previousTotal = roundAmount(map[categoryId] ?? 0);
      const expenseAmount = roundAmount(expense.amount);
      map[categoryId] = roundAmount(previousTotal + expenseAmount);
    });

    return map;
  }, [expenses]);

  const sortedExpenses = useMemo(
    () =>
      [...expenses]
        .sort(
          (a, b) => new Date(b.spent_at || 0).getTime() - new Date(a.spent_at || 0).getTime()
        )
        .slice(0, 8),
    [expenses]
  );

  const sortedPayments = useMemo(
    () =>
      [...payments]
        .sort(
          (a, b) => new Date(a.due_date || 0).getTime() - new Date(b.due_date || 0).getTime()
        )
        .slice(0, 8),
    [payments]
  );

  const sortedQuotes = useMemo(
    () =>
      [...quotes]
        .sort(
          (a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()
        )
        .slice(0, 8),
    [quotes]
  );

  const sortedAttachments = useMemo(
    () =>
      [...attachments]
        .sort(
          (a, b) =>
            new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        )
        .slice(0, 12),
    [attachments]
  );

  const categoryNameById = useMemo(() => {
    const map = {};
    categories.forEach((category) => {
      if (category?.id) {
        map[category.id] = category.name || category.key || `#${category.id}`;
      }
    });
    return map;
  }, [categories]);

  const quoteLabelById = useMemo(() => {
    const map = {};
    quotes.forEach((quote) => {
      if (!quote?.id) {
        return;
      }
      map[quote.id] = quote.title || quote.vendor_name || `Понуда #${quote.id}`;
    });
    return map;
  }, [quotes]);

  const openBudgetModal = () => {
    setBudgetForm((prev) => ({
      ...prev,
      currency: budget?.currency || prev.currency || "EUR",
      total_budget: String(roundAmount(budget?.total_budget)),
      notes: budget?.notes || "",
    }));
    setBudgetModalOpen(true);
  };

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const openMetricInfo = (event, text) => {
    setMetricInfoAnchorEl(event.currentTarget);
    setMetricInfoText(text);
  };

  const closeMetricInfo = () => {
    setMetricInfoAnchorEl(null);
    setMetricInfoText("");
  };

  const openQuoteEditModal = (quote) => {
    setQuoteEditForm({
      id: quote?.id || null,
      budget_category_id: String(quote?.budget_category_id || ""),
      vendor_name: quote?.vendor_name || "",
      amount: String(roundAmount(quote?.amount)),
      notes: quote?.notes || "",
      status: quote?.status || "draft",
      valid_until: toInputDate(quote?.valid_until),
    });
    setQuoteEditModalOpen(true);
  };

  const openExpenseEditModal = (expense) => {
    setExpenseEditForm({
      id: expense?.id || null,
      budget_category_id: String(expense?.budget_category_id || ""),
      vendor_quote_id: String(expense?.vendor_quote_id || ""),
      amount: String(roundAmount(expense?.amount)),
      spent_at: toInputDateTime(expense?.spent_at),
      notes: expense?.notes || "",
    });
    setExpenseEditModalOpen(true);
  };

  const attachmentEntityOptions = useMemo(() => {
    if (attachmentForm.entity_type === "expense") {
      return expenses.map((expense) => ({
        id: expense.id,
        label: `${expense.title || "Трошок"} | ${
          categoryNameById[expense.budget_category_id] || "Категорија"
        } | ${formatMoney(expense.amount, currency)}`,
      }));
    }

    if (attachmentForm.entity_type === "quote") {
      return quotes.map((quote) => ({
        id: quote.id,
        label: `${quote.title || quote.vendor_name || `Понуда #${quote.id}`} | ${
          quote.vendor_name || "Добавувач"
        } | ${formatMoney(quote.amount, currency)}`,
      }));
    }

    if (SHOW_PAYMENTS_PLANNER && attachmentForm.entity_type === "payment") {
      return payments.map((payment) => ({
        id: payment.id,
        label: `${payment.title || `Плаќање #${payment.id}`} | ${
          categoryNameById[payment.budget_category_id] || "Категорија"
        } | ${formatMoney(payment.amount_due, currency)}`,
      }));
    }

    return [];
  }, [attachmentForm.entity_type, expenses, quotes, payments, categoryNameById, currency]);

  const autoExpenseTitle = useMemo(() => {
    const categoryId = Number(expenseForm.budget_category_id);
    const categoryLabel = categoryNameById[categoryId] || "";
    if (!categoryLabel) {
      return "";
    }

    const quoteId = Number(expenseForm.vendor_quote_id);
    const quoteLabel = quoteId ? quoteLabelById[quoteId] || "" : "";

    if (quoteLabel) {
      return `${categoryLabel} - ${quoteLabel} - трошок`;
    }

    return `${categoryLabel} - трошок`;
  }, [
    expenseForm.budget_category_id,
    expenseForm.vendor_quote_id,
    categoryNameById,
    quoteLabelById,
  ]);

  const autoPaymentTitle = useMemo(() => {
    const categoryId = Number(paymentForm.budget_category_id);
    const categoryLabel = categoryNameById[categoryId] || "";
    if (!categoryLabel) {
      return "";
    }

    const quoteId = Number(paymentForm.vendor_quote_id);
    const quoteLabel = quoteId ? quoteLabelById[quoteId] || "" : "";

    if (quoteLabel) {
      return `${categoryLabel} - ${quoteLabel} - планер-плаќање`;
    }

    return `${categoryLabel} - планер-плаќање`;
  }, [
    paymentForm.budget_category_id,
    paymentForm.vendor_quote_id,
    categoryNameById,
    quoteLabelById,
  ]);

  const autoQuoteTitle = useMemo(() => {
    const categoryLabel =
      categoryNameById[Number(quoteForm.budget_category_id)] || "";
    const vendorLabel = String(quoteForm.vendor_name || "").trim();

    if (!categoryLabel || !vendorLabel) {
      return "";
    }

    return `${categoryLabel} - ${vendorLabel}`;
  }, [categoryNameById, quoteForm.budget_category_id, quoteForm.vendor_name]);

  useEffect(() => {
    if (!attachmentForm.entity_id) {
      return;
    }
    const exists = attachmentEntityOptions.some(
      (option) => String(option.id) === String(attachmentForm.entity_id)
    );
    if (!exists) {
      setAttachmentForm((prev) => ({ ...prev, entity_id: "" }));
    }
  }, [attachmentEntityOptions, attachmentForm.entity_id]);

  const handleBudgetCreate = async (event) => {
    event.preventDefault();
    const totalBudget = roundAmount(budgetForm.total_budget);

    if (totalBudget <= 0) {
      setError("Вкупниот буџет мора да биде поголем од нула.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await createWeddingFinanceBudget(weddingId, {
        currency: budgetForm.currency || "EUR",
        total_budget: totalBudget,
        notes: budgetForm.notes,
      });

      for (let index = 0; index < DEFAULT_CATEGORY_BLUEPRINT.length; index += 1) {
        const item = DEFAULT_CATEGORY_BLUEPRINT[index];
        await createWeddingFinanceCategory(weddingId, {
          key: item.key,
          name: item.name,
          allocated_amount: roundAmount(totalBudget * item.weight),
          sort_order: (index + 1) * 10,
        });
      }

      setNotice("Буџетот е креиран со почетни категории.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно креирање на буџет.");
    } finally {
      setSaving(false);
    }
  };

  const handleBudgetUpdate = async () => {
    if (!budget) {
      return;
    }

    const totalBudget = roundAmount(budgetForm.total_budget);
    if (totalBudget <= 0) {
      setError("Вкупниот буџет мора да биде поголем од нула.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinanceBudget(weddingId, {
        total_budget: totalBudget,
        notes: budgetForm.notes,
      });

      setNotice("Буџетот е ажуриран.");
      setBudgetModalOpen(false);
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на буџет.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();

    const keyCandidate = toCategoryKey(categoryForm.name);
    const name = categoryForm.name.trim();
    const allocatedAmount = roundAmount(categoryForm.allocated_amount);

    if (!name || !keyCandidate) {
      setError("Името на категоријата е задолжително.");
      return;
    }

    if (allocatedAmount < 0) {
      setError("Распределениот износ не смее да биде негативен.");
      return;
    }

    const hasDuplicateKey = categories.some(
      (category) => String(category?.key || "").toLowerCase() === keyCandidate
    );
    if (hasDuplicateKey) {
      setError("Клучот на категоријата веќе постои.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await createWeddingFinanceCategory(weddingId, {
        key: keyCandidate,
        name,
        allocated_amount: allocatedAmount,
        sort_order: categories.length * 10 + 10,
      });

      setCategoryForm({ name: "", allocated_amount: "" });
      setNotice("Категоријата е додадена.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно додавање категорија.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategory = async (category) => {
    const draft = categoryDrafts[category.id];
    const allocatedAmount = roundAmount(draft);
    const nameDraft = String(categoryNameDrafts[category.id] || "").trim();

    if (allocatedAmount < 0) {
      setError("Распределениот износ не смее да биде негативен.");
      return;
    }

    if (!nameDraft) {
      setError("Името на категоријата е задолжително.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinanceCategory(weddingId, category.id, {
        name: nameDraft,
        allocated_amount: allocatedAmount,
        sort_order: category.sort_order,
      });

      setNotice("Категоријата е ажурирана.");
      setEditingCategoryId(null);
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на категорија.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateQuote = async (event) => {
    event.preventDefault();

    const categoryId = Number(quoteForm.budget_category_id);
    const amount = roundAmount(quoteForm.amount);

    if (!categoryId || amount < 0 || !quoteForm.vendor_name.trim()) {
      setError("Понудата мора да има категорија, добавувач и ненегативен износ.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await createWeddingFinanceQuote(weddingId, {
        budget_category_id: categoryId,
        vendor_name: quoteForm.vendor_name.trim(),
        title: autoQuoteTitle || quoteForm.vendor_name.trim(),
        amount,
        notes: quoteForm.notes.trim(),
        status: quoteForm.status,
        valid_until: defaultValidUntilPlusOneMonth(),
      });

      setQuoteForm({
        budget_category_id: "",
        vendor_name: "",
        amount: "",
        notes: "",
        status: "draft",
      });
      setNotice("Понудата е додадена.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно додавање понуда.");
    } finally {
      setSaving(false);
    }
  };

  const handleQuoteStatusChange = async (quote, status) => {
    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinanceQuote(weddingId, quote.id, { status });
      setQuotes((prev) =>
        prev.map((item) => (item.id === quote.id ? { ...item, status } : item))
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на статусот на понудата.");
    } finally {
      setSaving(false);
    }
  };

  const handleQuoteEditSave = async () => {
    const quoteId = Number(quoteEditForm.id);
    const categoryId = Number(quoteEditForm.budget_category_id);
    const vendorName = String(quoteEditForm.vendor_name || "").trim();
    const amount = roundAmount(quoteEditForm.amount);

    if (!quoteId || !categoryId || !vendorName || amount < 0) {
      setError("Понудата мора да има категорија, добавувач и ненегативен износ.");
      return;
    }

    const categoryLabel = categoryNameById[categoryId] || "";
    const title = categoryLabel ? `${categoryLabel} - ${vendorName}` : vendorName;

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinanceQuote(weddingId, quoteId, {
        budget_category_id: categoryId,
        vendor_name: vendorName,
        title,
        amount,
        notes: String(quoteEditForm.notes || "").trim(),
        status: quoteEditForm.status || "draft",
        valid_until: quoteEditForm.valid_until || defaultValidUntilPlusOneMonth(),
      });

      setQuoteEditModalOpen(false);
      setNotice("Понудата е ажурирана.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на понуда.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePayment = async (event) => {
    event.preventDefault();

    const categoryId = Number(paymentForm.budget_category_id);
    const vendorQuoteId = paymentForm.vendor_quote_id
      ? Number(paymentForm.vendor_quote_id)
      : null;
    const amountDue = roundAmount(paymentForm.amount_due);
    const amountPaid = roundAmount(paymentForm.amount_paid);

    if (!categoryId) {
      setError("Категоријата за плаќање е задолжителна.");
      return;
    }

    if (amountDue < 0 || amountPaid < 0 || amountPaid > amountDue) {
      setError("Вредностите за плаќање не се валидни.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      const status = derivePaymentStatus(amountPaid, amountDue);

      await createWeddingFinancePayment(weddingId, {
        budget_category_id: categoryId,
        vendor_quote_id: vendorQuoteId,
        title: autoPaymentTitle || "Планер-плаќање",
        due_date: paymentForm.due_date,
        amount_due: amountDue,
        amount_paid: amountPaid,
        status,
      });

      setPaymentForm({
        budget_category_id: "",
        vendor_quote_id: "",
        due_date: toInputDate(),
        amount_due: "",
        amount_paid: "0",
      });
      setNotice("Плаќањето е додадено.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно додавање плаќање.");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePayment = async (payment) => {
    const amountDue = roundAmount(payment.amount_due);
    const amountPaid = roundAmount(paymentDrafts[payment.id]);

    if (amountPaid < 0 || amountPaid > amountDue) {
      setError("Платениот износ не смее да го надмине доспеаниот износ.");
      return;
    }

    const status = derivePaymentStatus(amountPaid, amountDue);

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinancePayment(weddingId, payment.id, {
        amount_paid: amountPaid,
        status,
      });

      setNotice("Плаќањето е ажурирано.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на плаќање.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateExpense = async (event) => {
    event.preventDefault();

    const categoryId = Number(expenseForm.budget_category_id);
    const vendorQuoteId = expenseForm.vendor_quote_id
      ? Number(expenseForm.vendor_quote_id)
      : null;
    const amount = roundAmount(expenseForm.amount);

    if (!categoryId) {
      setError("Категоријата за трошок е задолжителна.");
      return;
    }

    if (amount < 0) {
      setError("Износот на трошокот не смее да биде негативен.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await createWeddingFinanceExpense(weddingId, {
        budget_category_id: categoryId,
        vendor_quote_id: vendorQuoteId,
        title: autoExpenseTitle || "Трошок",
        amount,
        spent_at: toIsoDateTime(expenseForm.spent_at),
        notes: expenseForm.notes.trim(),
      });

      setExpenseForm({
        budget_category_id: "",
        vendor_quote_id: "",
        amount: "",
        spent_at: toInputDateTime(),
        notes: "",
      });
      setNotice("Трошокот е додаден.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно додавање трошок.");
    } finally {
      setSaving(false);
    }
  };

  const handleExpenseEditSave = async () => {
    const expenseId = Number(expenseEditForm.id);
    const categoryId = Number(expenseEditForm.budget_category_id);
    const vendorQuoteId = expenseEditForm.vendor_quote_id
      ? Number(expenseEditForm.vendor_quote_id)
      : null;
    const amount = roundAmount(expenseEditForm.amount);

    if (!expenseId || !categoryId) {
      setError("Категоријата за трошок е задолжителна.");
      return;
    }

    if (amount < 0) {
      setError("Износот на трошокот не смее да биде негативен.");
      return;
    }

    const categoryLabel = categoryNameById[categoryId] || "";
    const quoteLabel = vendorQuoteId ? quoteLabelById[vendorQuoteId] || "" : "";
    const title = quoteLabel
      ? `${categoryLabel} - ${quoteLabel} - трошок`
      : `${categoryLabel} - трошок`;

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await updateWeddingFinanceExpense(weddingId, expenseId, {
        budget_category_id: categoryId,
        vendor_quote_id: vendorQuoteId,
        title,
        amount,
        spent_at: toIsoDateTime(expenseEditForm.spent_at),
        notes: String(expenseEditForm.notes || "").trim(),
      });

      setExpenseEditModalOpen(false);
      setNotice("Трошокот е ажуриран.");
      await loadFinanceData({ silent: true });
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно ажурирање на трошок.");
    } finally {
      setSaving(false);
    }
  };

  const handleAttachmentUpload = async (event) => {
    event.preventDefault();

    const entityId = toEntityId(attachmentForm.entity_id);
    if (!entityId) {
      setError("Прилогот бара валиден ID на ентитет.");
      return;
    }

    if (!attachmentForm.file) {
      setError("Датотеката за прилог е задолжителна.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setNotice("");

      await createWeddingFinanceAttachment(weddingId, {
        entity_type: attachmentForm.entity_type,
        ...buildAttachmentEntityPayload(attachmentForm.entity_type, entityId),
        file: attachmentForm.file,
      });

      setAttachmentForm((prev) => ({
        ...prev,
        entity_id: "",
        file: null,
      }));
      setAttachmentInputKey((prev) => prev + 1);
      setNotice("Прилогот е прикачен.");
      await loadAttachments(attachmentFilter);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно прикачување прилог.");
    } finally {
      setSaving(false);
    }
  };

  const attachmentSection = (
    <Box className="finance-card finance-attachments-card">
      <Box className="finance-card-header finance-card-header-collapsible">
        <Box>
          <Typography variant="h6">Прилози</Typography>
          <Typography variant="body2">
            Прикачете сметки и документи како прилози поврзани со трошок, понуда или плаќање.
          </Typography>
        </Box>
        <IconButton
          size="small"
          className={`finance-collapse-button ${
            collapsedSections.attachments ? "is-collapsed" : ""
          }`}
          onClick={() => toggleSection("attachments")}
          aria-label={collapsedSections.attachments ? "Прошири прилози" : "Собери прилози"}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>

      {!collapsedSections.attachments && (
        <>
      <Box component="form" className="finance-attachment-form" onSubmit={handleAttachmentUpload}>
        <TextField
          select
          label="Тип на ентитет"
          value={attachmentForm.entity_type}
          onChange={(event) =>
            setAttachmentForm((prev) => ({
              ...prev,
              entity_type: event.target.value,
              entity_id: "",
            }))
          }
          required
        >
          {ATTACHMENT_ENTITY_TYPES.map((entityType) => (
            <MenuItem key={entityType} value={entityType}>
              {ENTITY_TYPE_LABELS[entityType] || entityType}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Избери ентитет"
          value={attachmentForm.entity_id}
          onChange={(event) =>
            setAttachmentForm((prev) => ({ ...prev, entity_id: event.target.value }))
          }
          required
        >
          {attachmentEntityOptions.length === 0 ? (
            <MenuItem value="" disabled>
              Нема достапни ставки
            </MenuItem>
          ) : (
            attachmentEntityOptions.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.label}
              </MenuItem>
            ))
          )}
        </TextField>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileIcon />}
          className="finance-file-button"
        >
          {attachmentForm.file ? "Промени датотека" : "Избери датотека"}
          <input
            key={attachmentInputKey}
            hidden
            type="file"
            onChange={(event) => {
              const selectedFile = event.target.files?.[0] || null;
              setAttachmentForm((prev) => ({ ...prev, file: selectedFile }));
            }}
          />
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving || attachmentEntityOptions.length === 0}
        >
          Прикачи
        </Button>
      </Box>

      {attachmentEntityOptions.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          Нема достапни {ENTITY_TYPE_LABELS[attachmentForm.entity_type]?.toLowerCase() || "ставки"} за избор.
        </Typography>
      ) : null}

      <Typography variant="caption" className="finance-file-caption">
        {attachmentForm.file
          ? `Selected file: ${attachmentForm.file.name}`
          : "Сѐ уште нема избрана датотека."}
      </Typography>

      <Divider className="finance-divider" />

      <Box className="finance-attachment-filter">
        <TextField
          select
          label="Филтер тип"
          value={attachmentFilter.entity_type}
          onChange={(event) => {
            const next = { entity_type: event.target.value };
            setAttachmentFilter(next);
            loadAttachments(next);
          }}
        >
          <MenuItem value="">Сите</MenuItem>
          {ATTACHMENT_ENTITY_TYPES.map((entityType) => (
            <MenuItem key={entityType} value={entityType}>
              {ENTITY_TYPE_LABELS[entityType] || entityType}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Stack spacing={1} mt={1.2}>
        {attachmentsLoading ? (
          <Box className="finance-loading-inline">
            <CircularProgress size={22} />
            <Typography variant="body2">Се вчитуваат прилозите...</Typography>
          </Box>
        ) : sortedAttachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Нема пронајдени прилози.
          </Typography>
        ) : (
          sortedAttachments.map((attachment) => {
            const entityId =
              attachment.expense_id ||
              attachment.vendor_quote_id ||
              attachment.payment_id ||
              "-";
            return (
              <Box className="finance-list-row compact finance-attachment-row" key={attachment.id}>
                <Box>
                  <Typography variant="subtitle2">
                    {ENTITY_TYPE_LABELS[attachment.entity_type] || "Прилог"} #{entityId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Прикачено: {toDateLabel(attachment.created_at)}
                  </Typography>
                </Box>
                <Button
                  href={attachment.file_url}
                  target="_blank"
                  rel="noreferrer"
                  size="small"
                  startIcon={<LinkIcon />}
                  className="finance-link-button"
                  disabled={!attachment.file_url}
                >
                  Отвори датотека
                </Button>
              </Box>
            );
          })
        )}
      </Stack>
        </>
      )}
    </Box>
  );

  if (!weddingId) {
    return (
      <Grid item className="organizer-tab-container" size={{ xs: 12, md: 10, lg: 9 }}>
        <Alert severity="warning">Нема избрана активна свадба.</Alert>
      </Grid>
    );
  }

  if (loading) {
    return (
      <Grid item className="organizer-tab-container" size={{ xs: 12, md: 10, lg: 9 }}>
        <Box className="finance-loading-wrap">
          <CircularProgress color="secondary" />
          <Typography variant="body2">Се вчитува финансискиот планер...</Typography>
        </Box>
      </Grid>
    );
  }

  if (!budget) {
    return (
      <Grid item className="organizer-tab-container finance-tab-grid" size={{ xs: 12, md: 10, lg: 8 }}>
        <Box className="finance-shell">
          {error ? <Alert severity="error">{error}</Alert> : null}
          {notice ? <Alert severity="success">{notice}</Alert> : null}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <Box className="finance-onboarding-card">
              <Typography className="finance-overline">Свадбени финансии</Typography>
              <Typography variant="h4" className="finance-onboarding-title">
                Започнете со буџет за помалку од една минута
              </Typography>
              <Typography className="finance-onboarding-subtitle">
                Поставете вкупен буџет и автоматски ќе се пополнат категории за следење на понуди, плаќања и трошоци.
              </Typography>
              <Box component="form" className="finance-form-grid" onSubmit={handleBudgetCreate}>
                <TextField
                  select
                  label="Валута"
                  value={budgetForm.currency}
                  onChange={(event) =>
                    setBudgetForm((prev) => ({ ...prev, currency: event.target.value }))
                  }
                  required
                >
                  {CURRENCY_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Вкупен буџет"
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={budgetForm.total_budget}
                  onChange={(event) =>
                    setBudgetForm((prev) => ({ ...prev, total_budget: event.target.value }))
                  }
                  required
                />
                <TextField
                  label="Белешки"
                  value={budgetForm.notes}
                  onChange={(event) =>
                    setBudgetForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  multiline
                  minRows={3}
                  className="finance-form-full"
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  className="finance-cta-button"
                >
                  {saving ? "Се креира..." : "Креирај буџет и категории"}
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Box>
      </Grid>
    );
  }

  return (
    <Grid item className="organizer-tab-container finance-tab-grid" size={{ xs: 12, md: 11, lg: 10, xl: 9 }}>
      <Box className="finance-shell">
        {error ? <Alert severity="error">{error}</Alert> : null}
        {notice ? <Alert severity="success">{notice}</Alert> : null}

        <motion.div
          className="finance-hero"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.36 }}
        >
          <Box>
            <Typography className="finance-overline">Планер за свадбени финансии</Typography>
            <Typography variant="h4" className="finance-title">
              Имајте јасен преглед каде оди буџетот
            </Typography>
            <Typography className="finance-subtitle">
              Следете распределба по категории, понуди, напредок на плаќања и реални трошоци на едно место.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} mt={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                className="finance-hero-button"
                onClick={() => {
                  loadFinanceData();
                  loadAttachments(attachmentFilter);
                }}
                disabled={saving}
              >
                Освежи
              </Button>
              <Button
                variant="outlined"
                className="finance-hero-button-secondary"
                onClick={openBudgetModal}
                disabled={saving}
              >
                Уреди буџет
              </Button>
            </Stack>
          </Box>
          <Box
            className="finance-ring"
            style={{ "--finance-progress": `${aggregates.spendProgress}%` }}
          >
            <Box className="finance-ring-inner">
              <Typography variant="body2" className="finance-ring-label">
                Преостанато
              </Typography>
              <Typography variant="h6" className="finance-ring-value">
                {formatMoney(aggregates.remainingBudget, currency)}
              </Typography>
              <Typography variant="caption" className="finance-ring-caption">
                Потрошено {aggregates.spendProgress.toFixed(0)}%
              </Typography>
            </Box>
          </Box>
        </motion.div>

        <Box
          className={`finance-metrics-grid ${
            SHOW_PAYMENTS_PLANNER ? "" : "finance-metrics-grid-no-payments"
          }`}
        >
          <Box className="finance-metric-card">
            <IconButton
              size="small"
              className="finance-metric-info"
              aria-label="Инфо за вкупен буџет"
              onClick={(event) =>
                openMetricInfo(event, "Вкупен износ што планирате да го потрошите за свадбата.")
              }
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            <SavingsIcon className="finance-metric-icon" />
            <Typography variant="caption">Вкупен буџет</Typography>
            <Box className="finance-metric-value-row">
              <Typography variant="h6">{formatMoney(aggregates.budgetTotal, currency)}</Typography>
              <IconButton
                size="small"
                className="finance-metric-edit"
                onClick={openBudgetModal}
                aria-label="Уреди буџет"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box className="finance-metric-card">
            <IconButton
              size="small"
              className="finance-metric-info"
              aria-label="Инфо за распределено"
              onClick={(event) =>
                openMetricInfo(event, "Вкупен износ што е распределен по сите категории.")
              }
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            <PaidIcon className="finance-metric-icon" />
            <Typography variant="caption">Распределено</Typography>
            <Typography variant="h6">{formatMoney(aggregates.allocatedTotal, currency)}</Typography>
          </Box>
          <Box className="finance-metric-card">
            <IconButton
              size="small"
              className="finance-metric-info"
              aria-label="Инфо за потрошено"
              onClick={(event) =>
                openMetricInfo(event, "Веќе потрошени финансиски средства од буџетот.")
              }
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
            <ReceiptLongIcon className="finance-metric-icon" />
            <Typography variant="caption">Потрошено</Typography>
            <Typography variant="h6">{formatMoney(aggregates.spentTotal, currency)}</Typography>
          </Box>
          {SHOW_PAYMENTS_PLANNER ? (
            <Box className="finance-metric-card">
              <IconButton
                size="small"
                className="finance-metric-info"
                aria-label="Инфо за исплатено"
                onClick={(event) =>
                  openMetricInfo(
                    event,
                    "Планер за плаќања = што треба/планираш да платиш (распоред по рати, рокови, следење колку е платено)."
                  )
                }
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
              <RequestQuoteIcon className="finance-metric-icon" />
              <Typography variant="caption">Исплатено</Typography>
              <Typography variant="h6">{formatMoney(aggregates.paymentsPaidTotal, currency)}</Typography>
            </Box>
          ) : null}
        </Box>
        <Popover
          open={Boolean(metricInfoAnchorEl)}
          anchorEl={metricInfoAnchorEl}
          onClose={closeMetricInfo}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 1.1, maxWidth: 320 }}>
            <Typography variant="body2">{metricInfoText}</Typography>
          </Box>
        </Popover>

        <motion.div
          className="finance-main-grid"
          variants={motionContainer}
          initial="hidden"
          animate="show"
        >
          <motion.section className="finance-card" variants={motionItem}>
            <Box className="finance-card-header finance-card-header-collapsible">
              <Box>
                <Typography variant="h6">Поставки за буџет</Typography>
                <Typography variant="body2">Изменете го вкупниот буџет и белешките.</Typography>
              </Box>
              <IconButton
                size="small"
                className={`finance-collapse-button ${
                  collapsedSections.budget ? "is-collapsed" : ""
                }`}
                onClick={() => toggleSection("budget")}
                aria-label={collapsedSections.budget ? "Прошири поставки за буџет" : "Собери поставки за буџет"}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            {!collapsedSections.budget && (
              <>
            <Box className="finance-budget-inline-summary">
              <Typography variant="body2" color="text.secondary">
                Валута: {currency}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Буџет: {formatMoney(aggregates.budgetTotal, currency)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Белешки: {budget?.notes ? budget.notes : "Нема белешки"}
              </Typography>
              <Button variant="contained" onClick={openBudgetModal} disabled={saving}>
                Уреди буџет
              </Button>
            </Box>
            <Divider className="finance-divider" />
            <Typography variant="subtitle1" className="finance-subsection-title">
              Категории
            </Typography>
            <Box component="form" className="finance-inline-form" onSubmit={handleCreateCategory}>
              <TextField
                label="Име"
                value={categoryForm.name}
                onChange={(event) =>
                  setCategoryForm((prev) => ({ ...prev, name: event.target.value }))
                }
                required
              />
              <TextField
                label="Распределено"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={categoryForm.allocated_amount}
                onChange={(event) =>
                  setCategoryForm((prev) => ({ ...prev, allocated_amount: event.target.value }))
                }
                required
              />
              <Button type="submit" variant="outlined" disabled={saving}>
                Додај категорија
              </Button>
            </Box>
            <Stack spacing={1.2} mt={1.4}>
              {categories.map((category) => {
                const allocated = roundAmount(category.allocated_amount);
                const spent = roundAmount(expensesByCategory[category.id]);
                const remaining = roundAmount(allocated - spent);
                const ratio = allocated > 0 ? Math.min((spent / allocated) * 100, 100) : 0;

                return (
                  <Box className="finance-list-row finance-category-row" key={category.id || category.key}>
                    <Box className="finance-row-top">
                      <Box>
                        {editingCategoryId === category.id ? (
                          <TextField
                            size="small"
                            label="Име"
                            value={categoryNameDrafts[category.id] ?? ""}
                            onChange={(event) =>
                              setCategoryNameDrafts((prev) => ({
                                ...prev,
                                [category.id]: event.target.value,
                              }))
                            }
                            onBlur={() => setEditingCategoryId(null)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                setEditingCategoryId(null);
                              }
                              if (event.key === "Escape") {
                                setCategoryNameDrafts((prev) => ({
                                  ...prev,
                                  [category.id]: category.name || category.key || "",
                                }));
                                setEditingCategoryId(null);
                              }
                            }}
                            autoFocus
                          />
                        ) : (
                          <Typography
                            variant="subtitle2"
                            className="finance-category-clickable"
                            onClick={() => setEditingCategoryId(category.id)}
                            title="Клик за промена на име"
                          >
                            {categoryNameDrafts[category.id] || category.name || category.key}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Потрошено {formatMoney(spent, currency)} / {formatMoney(allocated, currency)}
                        </Typography>
                      </Box>
                    </Box>
                    <LinearProgress variant="determinate" value={ratio} className="finance-progress" />
                    <Box className="finance-row-actions">
                      <TextField
                        size="small"
                        label="Распределено"
                        type="number"
                        inputProps={{ min: 0, step: "0.01" }}
                        value={categoryDrafts[category.id] ?? ""}
                        onChange={(event) =>
                          setCategoryDrafts((prev) => ({
                            ...prev,
                            [category.id]: event.target.value,
                          }))
                        }
                      />
                      <Typography variant="body2" color={remaining >= 0 ? "text.secondary" : "error"}>
                        {remaining >= 0
                          ? `Преостанато: ${formatMoney(remaining, currency)}`
                          : `Надминато: ${formatMoney(Math.abs(remaining), currency)}`}
                      </Typography>
                      <Button
                        variant="text"
                        onClick={() => handleSaveCategory(category)}
                        disabled={saving}
                      >
                        Зачувај
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
              </>
            )}
          </motion.section>

          <motion.section className="finance-card" variants={motionItem}>
            <Box className="finance-card-header finance-card-header-collapsible">
              <Box>
                <Typography variant="h6">Понуди од добавувачи</Typography>
                <Typography variant="body2">Вклучете во избор и одберете ја најдобрата понуда.</Typography>
              </Box>
              <IconButton
                size="small"
                className={`finance-collapse-button ${
                  collapsedSections.quotes ? "is-collapsed" : ""
                }`}
                onClick={() => toggleSection("quotes")}
                aria-label={collapsedSections.quotes ? "Прошири понуди" : "Собери понуди"}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            {!collapsedSections.quotes && (
              <>
            <Box component="form" className="finance-form-grid" onSubmit={handleCreateQuote}>
              <TextField
                select
                label="Категорија"
                value={quoteForm.budget_category_id}
                onChange={(event) =>
                  setQuoteForm((prev) => ({
                    ...prev,
                    budget_category_id: event.target.value,
                  }))
                }
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id || category.key} value={category.id}>
                    {category.name || category.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Добавувач"
                value={quoteForm.vendor_name}
                onChange={(event) =>
                  setQuoteForm((prev) => ({
                    ...prev,
                    vendor_name: event.target.value,
                  }))
                }
                required
              />
              <TextField
                label="Износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={quoteForm.amount}
                onChange={(event) =>
                  setQuoteForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                required
              />
              <TextField
                select
                label="Статус"
                value={quoteForm.status}
                onChange={(event) =>
                  setQuoteForm((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                {QUOTE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {QUOTE_STATUS_LABELS[status] || status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Белешки"
                value={quoteForm.notes}
                onChange={(event) =>
                  setQuoteForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={2}
                className="finance-form-full"
              />
              <Button type="submit" variant="contained" disabled={saving}>
                Додај понуда
              </Button>
            </Box>
            <Divider className="finance-divider" />
              <Typography variant="subtitle1" className="finance-subsection-title">
              Последни понуди
            </Typography>
            <Stack spacing={1}>
              {sortedQuotes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Сѐ уште нема понуди.
                </Typography>
              ) : (
                sortedQuotes.map((quote) => (
                  <Box className="finance-list-row" key={quote.id}>
                    <Box className="finance-row-top">
                      <Box>
                        <Typography variant="subtitle2">
                          {quote.title || quote.vendor_name || `Quote #${quote.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {categoryNameById[quote.budget_category_id] || "Категорија"} | {quote.vendor_name || "Добавувач"}
                        </Typography>
                      </Box>
                      <Box className="finance-inline-edit-wrap">
                        <Typography variant="subtitle2">{formatMoney(quote.amount, currency)}</Typography>
                        <IconButton
                          size="small"
                          className="finance-inline-edit"
                          onClick={() => openQuoteEditModal(quote)}
                          aria-label="Уреди понуда"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box className="finance-row-actions">
                      <TextField
                        select
                        size="small"
                        label="Статус"
                        value={quote.status || "draft"}
                        onChange={(event) =>
                          handleQuoteStatusChange(quote, event.target.value)
                        }
                      >
                        {QUOTE_STATUSES.map((status) => (
                          <MenuItem key={status} value={status}>
                          {QUOTE_STATUS_LABELS[status] || status}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Typography variant="caption" color="text.secondary">
                        Важи до: {toDateLabel(quote.valid_until)}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Stack>
              </>
            )}
            </motion.section>

          <motion.section className="finance-card" variants={motionItem}>
            <Box className="finance-card-header finance-card-header-collapsible">
              <Box>
                <Typography variant="h6">Трошоци</Typography>
                <Typography variant="body2">Реалните трошоци веднаш ги ажурираат вкупните суми.</Typography>
              </Box>
              <IconButton
                size="small"
                className={`finance-collapse-button ${
                  collapsedSections.expenses ? "is-collapsed" : ""
                }`}
                onClick={() => toggleSection("expenses")}
                aria-label={collapsedSections.expenses ? "Прошири трошоци" : "Собери трошоци"}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            {!collapsedSections.expenses && (
              <>
            <Box component="form" className="finance-form-grid" onSubmit={handleCreateExpense}>
              <TextField
                select
                label="Категорија"
                value={expenseForm.budget_category_id}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    budget_category_id: event.target.value,
                  }))
                }
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id || category.key} value={category.id}>
                    {category.name || category.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Поврзана понуда"
                value={expenseForm.vendor_quote_id}
                onChange={(event) =>
                  setExpenseForm((prev) => ({
                    ...prev,
                    vendor_quote_id: event.target.value,
                  }))
                }
              >
                <MenuItem value="">Нема</MenuItem>
                {quotes.map((quote) => (
                  <MenuItem key={quote.id} value={quote.id}>
                    {quote.title || quote.vendor_name || `Quote #${quote.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={expenseForm.amount}
                onChange={(event) =>
                  setExpenseForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                required
              />
              <TextField
                label="Потрошено на"
                type="datetime-local"
                value={expenseForm.spent_at}
                onChange={(event) =>
                  setExpenseForm((prev) => ({ ...prev, spent_at: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Белешки"
                value={expenseForm.notes}
                onChange={(event) =>
                  setExpenseForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={2}
                className="finance-form-full"
              />
              <Button type="submit" variant="contained" disabled={saving}>
                Додај трошок
              </Button>
            </Box>
            <Divider className="finance-divider" />
              <Typography variant="subtitle1" className="finance-subsection-title">
              Последни трошоци
            </Typography>
            <Stack spacing={1}>
              {sortedExpenses.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Сѐ уште нема трошоци.
                </Typography>
              ) : (
                sortedExpenses.map((expense) => (
                  <Box className="finance-list-row compact" key={expense.id}>
                    <Box>
                      <Typography variant="subtitle2">{expense.title || "Трошок"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {categoryNameById[expense.budget_category_id] || "Категорија"} | {toDateLabel(expense.spent_at)}
                      </Typography>
                    </Box>
                    <Box className="finance-inline-edit-wrap">
                      <Typography variant="subtitle2">{formatMoney(expense.amount, currency)}</Typography>
                      <IconButton
                        size="small"
                        className="finance-inline-edit"
                        onClick={() => openExpenseEditModal(expense)}
                        aria-label="Уреди трошок"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </Stack>
              </>
            )}
            </motion.section>

          {SHOW_PAYMENTS_PLANNER ? (
            <motion.section id="organizer-payments" tabIndex={-1} className="finance-card" variants={motionItem}>
            <Box className="finance-card-header finance-card-header-collapsible">
              <Box>
                <Typography variant="h6">Планер за плаќања</Typography>
                <Typography variant="body2">Статусот автоматски се одредува според уплатениот износ.</Typography>
              </Box>
              <IconButton
                size="small"
                className={`finance-collapse-button ${
                  collapsedSections.payments ? "is-collapsed" : ""
                }`}
                onClick={() => toggleSection("payments")}
                aria-label={collapsedSections.payments ? "Прошири плаќања" : "Собери плаќања"}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            {!collapsedSections.payments && (
              <>
            <Box component="form" className="finance-form-grid" onSubmit={handleCreatePayment}>
              <TextField
                select
                label="Категорија"
                value={paymentForm.budget_category_id}
                onChange={(event) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    budget_category_id: event.target.value,
                  }))
                }
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id || category.key} value={category.id}>
                    {category.name || category.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Поврзана понуда"
                value={paymentForm.vendor_quote_id}
                onChange={(event) =>
                  setPaymentForm((prev) => ({
                    ...prev,
                    vendor_quote_id: event.target.value,
                  }))
                }
              >
                <MenuItem value="">Нема</MenuItem>
                {quotes.map((quote) => (
                  <MenuItem key={quote.id} value={quote.id}>
                    {quote.title || quote.vendor_name || `Quote #${quote.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Датум на доспевање"
                type="date"
                value={paymentForm.due_date}
                onChange={(event) =>
                  setPaymentForm((prev) => ({ ...prev, due_date: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                label="Доспеан износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={paymentForm.amount_due}
                onChange={(event) =>
                  setPaymentForm((prev) => ({ ...prev, amount_due: event.target.value }))
                }
                required
              />
              <TextField
                label="Уплатен износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={paymentForm.amount_paid}
                onChange={(event) =>
                  setPaymentForm((prev) => ({ ...prev, amount_paid: event.target.value }))
                }
                required
              />
              <Button type="submit" variant="contained" disabled={saving}>
                Додај плаќање
              </Button>
            </Box>
            <Divider className="finance-divider" />
              <Typography variant="subtitle1" className="finance-subsection-title">
              Претстојни плаќања
            </Typography>
            <Stack spacing={1}>
              {sortedPayments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Сѐ уште нема плаќања.
                </Typography>
              ) : (
                sortedPayments.map((payment) => {
                  const amountDue = roundAmount(payment.amount_due);
                  const amountPaid = roundAmount(payment.amount_paid);
                  const ratio = amountDue > 0 ? Math.min((amountPaid / amountDue) * 100, 100) : 0;
                  const status = payment.status || derivePaymentStatus(amountPaid, amountDue);

                  return (
                    <Box className="finance-list-row" key={payment.id}>
                      <Box className="finance-row-top">
                        <Box>
                          <Typography variant="subtitle2">{payment.title || "Плаќање"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {categoryNameById[payment.budget_category_id] || "Категорија"} | Доспева: {toDateLabel(payment.due_date)}
                          </Typography>
                        </Box>
                        <Chip size="small" label={PAYMENT_STATUS_LABELS[status] || status} />
                      </Box>
                      <LinearProgress variant="determinate" value={ratio} className="finance-progress" />
                      <Box className="finance-row-actions">
                        <TextField
                          size="small"
                          label="Уплатено"
                          type="number"
                          inputProps={{ min: 0, step: "0.01" }}
                          value={paymentDrafts[payment.id] ?? ""}
                          onChange={(event) =>
                            setPaymentDrafts((prev) => ({
                              ...prev,
                              [payment.id]: event.target.value,
                            }))
                          }
                        />
                        <Typography variant="body2" color="text.secondary">
                          Доспеано: {formatMoney(amountDue, currency)}
                        </Typography>
                        <Button
                          variant="text"
                          onClick={() => handleSavePayment(payment)}
                          disabled={saving}
                        >
                          Зачувај
                        </Button>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Stack>
              </>
            )}
            </motion.section>
          ) : null}

          <motion.div variants={motionItem}>{attachmentSection}</motion.div>
        </motion.div>

        <Dialog
          open={quoteEditModalOpen}
          onClose={() => setQuoteEditModalOpen(false)}
          TransitionComponent={Transition}
          keepMounted
          PaperProps={{ className: "custom-dialog-paper finance-budget-dialog-paper" }}
        >
          <DialogTitle className="finance-budget-dialog-title">Уреди понуда</DialogTitle>
          <DialogContent dividers>
            <Box className="finance-form-grid finance-budget-dialog-form">
              <TextField
                select
                label="Категорија"
                value={quoteEditForm.budget_category_id}
                onChange={(event) =>
                  setQuoteEditForm((prev) => ({
                    ...prev,
                    budget_category_id: event.target.value,
                  }))
                }
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id || category.key} value={category.id}>
                    {category.name || category.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Добавувач"
                value={quoteEditForm.vendor_name}
                onChange={(event) =>
                  setQuoteEditForm((prev) => ({ ...prev, vendor_name: event.target.value }))
                }
                required
              />
              <TextField
                label="Износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={quoteEditForm.amount}
                onChange={(event) =>
                  setQuoteEditForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                required
              />
              <TextField
                select
                label="Статус"
                value={quoteEditForm.status}
                onChange={(event) =>
                  setQuoteEditForm((prev) => ({ ...prev, status: event.target.value }))
                }
              >
                {QUOTE_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {QUOTE_STATUS_LABELS[status] || status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Белешки"
                value={quoteEditForm.notes}
                onChange={(event) =>
                  setQuoteEditForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={3}
                className="finance-form-full"
              />
            </Box>
          </DialogContent>
          <DialogActions className="finance-budget-dialog-actions">
            <Button
              variant="outlined"
              onClick={() => setQuoteEditModalOpen(false)}
              disabled={saving}
            >
              Откажи
            </Button>
            <Button
              variant="contained"
              onClick={handleQuoteEditSave}
              disabled={saving}
              className="finance-budget-save-button"
            >
              {saving ? "Се зачувува..." : "Зачувај"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={expenseEditModalOpen}
          onClose={() => setExpenseEditModalOpen(false)}
          TransitionComponent={Transition}
          keepMounted
          PaperProps={{ className: "custom-dialog-paper finance-budget-dialog-paper" }}
        >
          <DialogTitle className="finance-budget-dialog-title">Уреди трошок</DialogTitle>
          <DialogContent dividers>
            <Box className="finance-form-grid finance-budget-dialog-form">
              <TextField
                select
                label="Категорија"
                value={expenseEditForm.budget_category_id}
                onChange={(event) =>
                  setExpenseEditForm((prev) => ({
                    ...prev,
                    budget_category_id: event.target.value,
                  }))
                }
                required
              >
                {categories.map((category) => (
                  <MenuItem key={category.id || category.key} value={category.id}>
                    {category.name || category.key}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Поврзана понуда"
                value={expenseEditForm.vendor_quote_id}
                onChange={(event) =>
                  setExpenseEditForm((prev) => ({
                    ...prev,
                    vendor_quote_id: event.target.value,
                  }))
                }
              >
                <MenuItem value="">Нема</MenuItem>
                {quotes.map((quote) => (
                  <MenuItem key={quote.id} value={quote.id}>
                    {quote.title || quote.vendor_name || `Quote #${quote.id}`}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Износ"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={expenseEditForm.amount}
                onChange={(event) =>
                  setExpenseEditForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                required
              />
              <TextField
                label="Потрошено на"
                type="datetime-local"
                value={expenseEditForm.spent_at}
                onChange={(event) =>
                  setExpenseEditForm((prev) => ({ ...prev, spent_at: event.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Белешки"
                value={expenseEditForm.notes}
                onChange={(event) =>
                  setExpenseEditForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={3}
                className="finance-form-full"
              />
            </Box>
          </DialogContent>
          <DialogActions className="finance-budget-dialog-actions">
            <Button
              variant="outlined"
              onClick={() => setExpenseEditModalOpen(false)}
              disabled={saving}
            >
              Откажи
            </Button>
            <Button
              variant="contained"
              onClick={handleExpenseEditSave}
              disabled={saving}
              className="finance-budget-save-button"
            >
              {saving ? "Се зачувува..." : "Зачувај"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={budgetModalOpen}
          onClose={() => setBudgetModalOpen(false)}
          TransitionComponent={Transition}
          keepMounted
          PaperProps={{ className: "custom-dialog-paper finance-budget-dialog-paper" }}
        >
          <DialogTitle className="finance-budget-dialog-title">Уреди буџет</DialogTitle>
          <DialogContent dividers>
            <Box className="finance-form-grid finance-budget-dialog-form">
              <TextField
                label="Вкупен буџет"
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                value={budgetForm.total_budget}
                onChange={(event) =>
                  setBudgetForm((prev) => ({ ...prev, total_budget: event.target.value }))
                }
              />
              <TextField
                label="Белешки"
                value={budgetForm.notes}
                onChange={(event) =>
                  setBudgetForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                multiline
                minRows={4}
                className="finance-form-full"
              />
            </Box>
          </DialogContent>
          <DialogActions className="finance-budget-dialog-actions">
            <Button
              variant="outlined"
              onClick={() => setBudgetModalOpen(false)}
              disabled={saving}
            >
              Откажи
            </Button>
            <Button
              variant="contained"
              onClick={handleBudgetUpdate}
              disabled={saving}
              className="finance-budget-save-button"
            >
              {saving ? "Се зачувува..." : "Зачувај"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Grid>
  );
};

export default FinanceTab;
